/**
 * Shared enums — client + server. Every enum here is also a state set
 * on the lifecycle engine (see src/lifecycle/engine.ts).
 *
 * Ported from .handoff/design_handoff_prospect_management/API_SPEC.md
 * and SCHEMA_PROJECTS_TASKS.md.
 */

// ─── Platform roles (RBAC) ───────────────────────────────────────
// Five-tier model, ranked low→high; higher tiers inherit lower capabilities.
//   super_admin  platform owner — backend/secrets/API management (DoubleXL)
//   owner        manage all tenant settings + full data CRUD (tenant owner)
//   admin        full data CRUD + team/assignment/role management
//   member       "Team" — browses every client, works the tasks they're on
//   read_only    reads everything, mutates nothing
export const ROLES = ['read_only', 'member', 'admin', 'owner', 'super_admin'] as const
export type Role = (typeof ROLES)[number]

export const ROLE_LABEL: Record<Role, string> = {
  read_only: 'Read-only',
  member: 'Team',
  admin: 'Admin',
  owner: 'Owner',
  super_admin: 'Super Admin',
}

export const ROLE_RANK: Record<Role, number> = {
  read_only: 0,
  member: 1,
  admin: 2,
  owner: 3,
  super_admin: 4,
}

// Capability predicates — the single source of truth shared by the API
// middleware (src/api/middleware/permissions.ts) and the SPA. Take plain
// strings so callers don't need to narrow an untrusted DB value first.
const SEE_ALL = new Set<string>(['super_admin', 'owner', 'admin', 'read_only'])
const MANAGE = new Set<string>(['super_admin', 'owner', 'admin'])
const WRITE = new Set<string>(['super_admin', 'owner', 'admin', 'member'])
const PLATFORM = new Set<string>(['super_admin'])
const FINANCIALS = new Set<string>(['super_admin', 'owner'])
// The Team role (Q-LXPV2.1, LXP-55): a `member` works across the whole agency,
// so the client directory and the roster are open to them — but *tasks* stay
// assignment-scoped, and the commercial record (contracts, MRR) stays closed.
const CLIENT_DIRECTORY = new Set<string>(['super_admin', 'owner', 'admin', 'member', 'read_only'])
const FULL_ROSTER = new Set<string>(['super_admin', 'owner', 'admin', 'member', 'read_only'])

/** Sees every entity (no assignment scoping). */
export const roleCanSeeAll = (role: string): boolean => SEE_ALL.has(role)
/** Manages the org: team roster, assignments, role changes. */
export const roleCanManage = (role: string): boolean => MANAGE.has(role)
/** May create/update/delete resource data (excludes read_only). */
export const roleCanWrite = (role: string): boolean => WRITE.has(role)
/** Platform-level backend administration (super_admin only). */
export const roleIsPlatform = (role: string): boolean => PLATFORM.has(role)
/** Can see financial data (MRR, contract values). Owner + super_admin only. */
export const roleCanSeeFinancials = (role: string): boolean => FINANCIALS.has(role)
/**
 * Opens every client record and page (overview, notes, files, links) without
 * an assignment. Financials are still stripped below owner, and client
 * *mutations* stay behind `roleCanManage`.
 */
export const roleCanBrowseClients = (role: string): boolean => CLIENT_DIRECTORY.has(role)
/**
 * Sees every roster row (columns are still redacted below admin by
 * `src/shared/employee-view.ts`) and may assign anyone on it to a task they're
 * on. Without it, rows narrow to co-assignees (`src/shared/teammate-scope.ts`).
 */
export const roleSeesFullRoster = (role: string): boolean => FULL_ROSTER.has(role)
/** Reads contracts — the commercial record. Not the Team role. */
export const roleCanSeeContracts = (role: string): boolean => SEE_ALL.has(role)
/** Permanently deletes a task. Everyone else completes or archives. */
export const roleCanDeleteTasks = (role: string): boolean => MANAGE.has(role)

// ─── Employee invite status ──────────────────────────────────────
export const INVITE_STATUSES = ['active', 'pending', 'roster_only'] as const
export type InviteStatus = (typeof INVITE_STATUSES)[number]

export const INVITE_STATUS_LABEL: Record<InviteStatus, string> = {
  active: 'Active',
  pending: 'Invited',
  roster_only: 'Roster only',
}

// ─── Prospect ────────────────────────────────────────────────────
export const STAGES = [
  'captured',
  'researching',
  'outreach',
  'intalks',
  'proposal',
  'won',
  'lost',
] as const
export type Stage = (typeof STAGES)[number]

export const STAGE_DOT: Record<Stage, string> = {
  captured: '#9AA4AE',
  researching: '#00D1FF',
  outreach: '#E0A93B',
  intalks: '#3FB68B',
  proposal: '#00D1FF',
  won: '#3FB68B',
  lost: '#E5484D',
}

export const WON_REASONS = [
  'Signed — monthly retainer',
  'Signed — one-time project',
  'Signed — Answer ($99/mo)',
  'Referral converted',
  'Upsell from existing',
] as const

export const LOST_REASONS = [
  'No budget',
  'Went silent / ghosted',
  'Chose a competitor',
  'Bad timing',
  'Not a fit',
  'Doing it in-house',
] as const

// ─── Client ──────────────────────────────────────────────────────
export const HEALTHS = ['good', 'watch', 'at-risk'] as const
export type Health = (typeof HEALTHS)[number]

// ─── Contract / SOW ──────────────────────────────────────────────
export const CONTRACT_STATUSES = [
  'draft',
  'active',
  'renewing',
  'expired',
  'cancelled',
] as const
export type ContractStatus = (typeof CONTRACT_STATUSES)[number]

export const CSTATUS: Record<
  ContractStatus,
  { label: string; tone: 'neutral' | 'success' | 'warning' | 'danger' }
> = {
  draft: { label: 'Draft', tone: 'neutral' },
  active: { label: 'Active', tone: 'success' },
  renewing: { label: 'Renewing', tone: 'warning' },
  expired: { label: 'Expired', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
}

export const TERMS = ['monthly', 'quarterly', 'annual', 'fixed'] as const
export type Term = (typeof TERMS)[number]

export const TERM: Record<Term, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
  fixed: 'One-off',
}

export const TIERS = ['basic', 'growth', 'full'] as const
export type Tier = (typeof TIERS)[number]

export const TIER: Record<Tier, string> = {
  basic: 'Basic',
  growth: 'Growth',
  full: 'Full',
}

export const TIER_RANK: Record<Tier, number> = { basic: 0, growth: 1, full: 2 }

// ─── Project ─────────────────────────────────────────────────────
export const PROJECT_STATUSES = ['planning', 'active', 'on_hold', 'complete'] as const
export type ProjectStatus = (typeof PROJECT_STATUSES)[number]

// ─── Task ────────────────────────────────────────────────────────
export const TASK_STATUSES = ['todo', 'in_progress', 'review', 'done', 'blocked'] as const
export type TaskStatus = (typeof TASK_STATUSES)[number]

export const PRIORITIES = ['low', 'med', 'high', 'urgent'] as const
export type Priority = (typeof PRIORITIES)[number]

// Disciplines are tenant-configurable — this is only the seed list for a fresh
// tenant, never the set of legal values. Read the live list from
// `GET /api/settings` (SPA: `useDisciplines()`); validate with
// `checkDiscipline()` in `src/shared/disciplines.ts`.
export { DEFAULT_DISCIPLINES as DISCIPLINES } from './disciplines'
export type Discipline = string

export { DEFAULT_DISCIPLINES as MODULES } from './disciplines'
export type Module = Discipline

// ─── Assignment ──────────────────────────────────────────────────
export const ASSIGNMENT_ROLES = [
  'account_manager',
  'lead',
  'contributor',
  'reviewer',
] as const
export type AssignmentRole = (typeof ASSIGNMENT_ROLES)[number]

// ─── Channels ────────────────────────────────────────────────────
export const CHANNEL_TYPES = ['email', 'sms', 'calendar', 'slack', 'whatsapp'] as const
export type ChannelType = (typeof CHANNEL_TYPES)[number]

// ─── Capture source ──────────────────────────────────────────────
export const CAPTURE_SOURCES = [
  'Referral',
  'Web form',
  'Email',
  'SMS',
  'Cold outreach',
  'Event',
] as const

// ─── Task window (grouping bucket) ───────────────────────────────
export const TASK_WINDOWS = ['today', 'week'] as const
export type TaskWindow = (typeof TASK_WINDOWS)[number]

// ─── Needs-you card ──────────────────────────────────────────────
export const NEED_TYPES = ['Follow-up', 'Reply', 'Meeting', 'Proposal'] as const
export type NeedType = (typeof NEED_TYPES)[number]

// ─── Attachments ─────────────────────────────────────────────────
export const ATTACHMENT_KINDS = ['email', 'drive', 'doc', 'calendar', 'link'] as const
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number]
